import { useEffect, useState, useRef } from "react";
import { ClipboardDocumentIcon, PencilSquareIcon } from '@heroicons/react/24/outline';
import { getStatusColor } from "../helpers/statusColor";
import PaymentBar from "./PaymentBar";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import { createNewPayment, deletePayment } from "../slices/paymentsSlice";
import { allPaymentTypes, BALANCE, CREDIT, DEPOSIT, DEPOSIT1, DEPOSIT2, enumPaymentTypes, FULL, SHIPPING, DISCOUNT } from "../consts/paymentTypes";
import { allTypesExist, getFirstNonMatchingItem } from "../helpers/sidebar";
import { LoaderSpinner } from "./LoaderSpinner";
import { PurchaseOrdersLoadingKeys, getAllPO, getPOLedgerDetailRequest } from "../slices/purchasesOrdersSlice";
import { toast } from "react-toastify";
import { POSidebarHeader } from "./POSidebar/POSidebarHeader";
import { POSidebarSummaryItems } from "./POSidebar/POSidebarSummaryItems";
import { OverlayGrayBackground } from "./OverlayGrayBackground";
import { LANDED_COST, QUANTITY, SHIPPING_COST, TOTAL_OWED, TOTAL_PAID, TOTAL_VALUE, SHIPPING_COST_EACH, LANDED_COST_FORECAST } from "./consts";
import { formatDateToShortLabel, formatPrice, getMissingQuantityTextColor, getTotalOwedTextColor } from "../helpers/common";
import { AttachmentsSection } from "./finance/AttachmentsSection";
import { updatePOById } from "../slices/purchasesOrdersSlice";
import { financePagePOTooltip } from "../helpers/tooltip";
import { POSidebarInformation } from "./POSidebar/POSidebarInformation";
import { CubeIcon, CurrencyDollarIcon, DocumentMinusIcon, DocumentPlusIcon } from "@heroicons/react/24/solid";
import { PaymentDialog } from "./finance/PaymentDialog";
import { FreightDialog } from "./finance/FreightDialog";
import { POSidebarNotes } from "./POSidebar/POSidebarNotes";
import { POSidebarAuditTrail } from "./POSidebar/POSidebarAuditTrail";
import { enumStatuses, OPEN } from "../consts/status";

export const POSidebar = ({
  po,
  access,
  onClose,
  searchValue,
  statuses,
  poPage,
  poItemsPerPage,
  testId,
  testData
}) => {
  const sidebarRef = useRef(null);
  const [currentPOId, setCurrentPO] = useState(po?.id || "");
  const [payments, setPayments] = useState(po.payments || []);
  const { loading: loadingPayments } = useSelector((state) => state.payments);
  const [openedStatus, openTheStatusSelect] = useState(false);
  const { loading: loadingPurchaseOrders, loadingKeys: purchaseOrdersLoadingKeys } = useSelector((state) => state.purchasesOrders);
  const poLedgerDetail = useSelector((state) => state.purchasesOrders.poLedgerDetail);
  const [freightActionError, setFreightActionError] = useState('');
  const [freightDialog, setFreightDialog] = useState({ open: false, mode: 'estimate', amount: '' });
  const [editingPayment, setEditingPayment] = useState(testId && testData?.editingPayment ? testData.editingPayment : null);
  const freightRefreshTimerRef = useRef(null);
  const dispatch = useDispatch();

  useEffect(() => {
    if (!testId && po?.id) dispatch(getPOLedgerDetailRequest({ poId: po.id }));
  }, [po?.id, testId, dispatch]);

  const refreshFreightDetail = () => dispatch(getPOLedgerDetailRequest({ poId: po.id }));
  const refreshPurchaseOrders = () => {
    if (testId) return;
    return dispatch(getAllPO({
      search: searchValue,
      statuses,
      page: poPage,
      itemsPerPage: poItemsPerPage,
    }));
  };
  const scheduleFreightRefresh = () => {
    if (freightRefreshTimerRef.current) clearTimeout(freightRefreshTimerRef.current);
    freightRefreshTimerRef.current = setTimeout(() => {
      freightRefreshTimerRef.current = null;
      refreshFreightDetail();
      refreshPurchaseOrders();
    }, 1500);
  };
  useEffect(() => () => {
    if (freightRefreshTimerRef.current) clearTimeout(freightRefreshTimerRef.current);
  }, []);
  const formatFreightDate = (value) => {
    if (!value) return '';
    const dateOnlyMatch = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (dateOnlyMatch) return `${dateOnlyMatch[2]}/${dateOnlyMatch[3]}/${dateOnlyMatch[1]}`;
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    return `${String(date.getMonth() + 1).padStart(2, '0')}/${String(date.getDate()).padStart(2, '0')}/${date.getFullYear()}`;
  };
  const runFreightAction = async (action) => {
    setFreightActionError('');
    try {
      await action();
      refreshFreightDetail();
      refreshPurchaseOrders();
    } catch (error) {
      setFreightActionError(error.response?.data?.error || error.message || 'Unable to update freight');
    }
  };

  const currentPurchaseOrdersLoadingKeys = testId && testData?.loadingKeys ? testData?.loadingKeys : purchaseOrdersLoadingKeys;
  const paymentStatusAllowed = po?.status === OPEN;
  const paymentStatusMessage = (action) =>
    `Please set this PO to Open to ${action} a payment. Current status: ${enumStatuses[po?.status] || po?.status || 'Unknown'}.`;

  const showPaymentStatusError = (action) => {
    toast.error(paymentStatusMessage(action), { position: "bottom-right" });
  };

  useEffect(() => {
    if (testId) return
    const handleScroll = (e) => {
      if (sidebarRef.current && sidebarRef.current.contains(e.target)) {
        e.stopPropagation();
      }
    };
    document.addEventListener('scroll', handleScroll, true);
    return () => {
      document.removeEventListener('scroll', handleScroll, true);
    };
  }, []);

  useEffect(() => {
    if (testId) return
    if (currentPOId !== po?.id) {
      openTheStatusSelect(false);
      setEditingPayment(null);
      setCurrentPO(po.id);
    }
  }, [po?.id])

  useEffect(() => {
    if (testId) return
    setPayments(po.payments || [])
    openTheStatusSelect(false)
  }, [po.payments])

  const handleEditClick = (payment) => {
    if (!paymentStatusAllowed) {
      showPaymentStatusError('edit');
      return;
    }
    setEditingPayment({
      ...payment,
      date: payment.date ? String(payment.date).slice(0, 10) : ""
    });
  };

  // const hasAnAbilityToSavePaymentByPrice = () => {
  //   switch (editingPayment.type) {
  //     case (DEPOSIT1): return ({ success: Number(po.paymentBarMilestones[DEPOSIT1]) === Number(editingPayment.amount), typePrice: po.paymentBarMilestones[DEPOSIT1] })
  //     case (DEPOSIT2): return ({ success: Number(po.paymentBarMilestones[DEPOSIT2]) === Number(editingPayment.amount), typePrice: po.paymentBarMilestones[DEPOSIT2] })
  //     case (BALANCE): return ({ success: Number(po.paymentBarMilestones[BALANCE]) === Number(editingPayment.amount), typePrice: po.paymentBarMilestones[BALANCE] })
  //     case (CREDIT): return ({ success: Number(po.paymentBarMilestones[CREDIT]) === Number(editingPayment.amount), typePrice: po.paymentBarMilestones[CREDIT] })
  //     case (SHIPPING): return ({ success: true })
  //     default: return ({ success: false })
  //   }
  // }

  const handleSaveClick = (paymentForm) => {
    if (access === "edit") {
      const currentPayment = paymentForm || editingPayment;
      if (!paymentStatusAllowed) {
        showPaymentStatusError(currentPayment?.id ? 'edit' : 'add');
        return;
      }
      if (testId) {
        console.log("save button clicked!")
        return
      }
      if (currentPayment?.type === DISCOUNT && Number(currentPayment.amount) >= 0) {
        toast.error("Discount amount must be less than 0", { position: "bottom-right" });
        return;
      }
      const newPayment = {
        type: currentPayment.type,
        transaction_id: currentPayment.transactionId || null,
        note: currentPayment.note || null,
        date_paid: currentPayment.date,
        amount: currentPayment.amount !== undefined ? currentPayment.amount : 0,
        po_id: po.id
      }
      if (currentPayment.id) {
        newPayment.id = currentPayment.id
      }
      dispatch(createNewPayment({
        payment: newPayment,
        poId: po.id,
        search: searchValue,
        statuses,
        page: poPage,
        itemsPerPage: poItemsPerPage
      })).then(() => scheduleFreightRefresh());
      setEditingPayment(null);
    }
  };

  const handleCancelClick = () => {
    if (testId) {
      console.log("cancel button clicked!")
      return
    }
    setEditingPayment(null);
  };

  const getFirstNonExistentType = () => {
    if (!payments || Array.isArray(payments) && payments.length === 0) return DEPOSIT1
    const option = getFirstNonMatchingItem(allPaymentTypes, payments);
    return option || DEPOSIT1
  }

  const handleAddPayment = () => {
    if (access === "edit") {
      if (!paymentStatusAllowed) {
        showPaymentStatusError('add');
        return;
      }
      if (testId) {
        console.log("add payment button clicked!")
        return
      }
      const newPayment = {
        id: null,
        date: new Date().toISOString().split('T')[0],
        amount: "",
        type: getFirstNonExistentType() || DEPOSIT1
      };
      setEditingPayment(newPayment);
    }
  };

  const handleDeletePayment = (paymentId) => {
    if (access === "edit") {
      if (testId) {
        console.log("delete payment button clicked!")
        return
      }
      dispatch(deletePayment({
        id: paymentId,
        search: searchValue,
        statuses,
        page: poPage,
        itemsPerPage: poItemsPerPage,
        poId: po.id
      })).then(() => scheduleFreightRefresh());
      setEditingPayment(null);
    }
  };

  const handleCopyEvent = (data) => {
    navigator.clipboard.writeText(data).then(() => {
      // You can add a toast notification here if you want to inform the user that the ID was copied
      console.log('Data copied to clipboard');
    });
  };

  const totalPaid = po.amountPaid || 0;
  const totalOwed = po.amountOwed || 0;
  const shippingCost = po.shippingCost || 0;
  const shippingCostEach = po.shippingCostEach || 0;
  const tariffCostEach = po.tariffCostEach || 0;
  const landedCostForecast = po.landedCostForecast || 0;
  const getSummaryItems = () => {
    // Helper function to get paid amount by payment type
    const getPaidAmount = (type) => {
      return (payments || [])
        .filter(p => p.type === type)
        .reduce((acc, payment) => acc + Number(payment.amount || 0), 0);
    };
    
    // Get deposit paid amounts combining different deposit types
    const depositPaidAmount = getPaidAmount(DEPOSIT) + getPaidAmount(DEPOSIT1) + getPaidAmount(DEPOSIT2);
    const balancePaidAmount = getPaidAmount(BALANCE);
    const creditPaidAmount = getPaidAmount(CREDIT);
    
    let summaryItems = [
      // First row
      { 
        icon: <CurrencyDollarIcon className="h-4 w-4" />,
        label: TOTAL_PAID, 
        value: `${totalPaid}`, 
        withTooltip: true, 
        tooltipText: "Total paid amount with shipping cost (excluding discount)", 
        tooltipAlign: 'text-left', 
        gridPosition: 'top-left',
        lineItems: [
          {
            label: "Deposit", 
            value: `${po?.hasFullPayment ? `$${formatPrice(po.paymentBarMilestones?.[DEPOSIT] || "0")}` : `$${formatPrice(depositPaidAmount.toString())}`}`, 
            compareValue: `$${formatPrice(po.paymentBarMilestones?.[DEPOSIT] || "0")}`
          },
          {
            label: "Balance", 
            value: `${po?.hasFullPayment ? `$${formatPrice(po.paymentBarMilestones?.[BALANCE] || "0")}` : `$${formatPrice(balancePaidAmount.toString())}`}`, 
            compareValue: `$${formatPrice(po.paymentBarMilestones?.[BALANCE] || "0")}`
          },
          {
            label: "Credit", 
            value: `${po?.hasFullPayment ? `$${formatPrice(po.paymentBarMilestones?.[CREDIT] || "0")}` : `$${formatPrice(creditPaidAmount.toString())}`}`, 
            compareValue: `$${formatPrice(po.paymentBarMilestones?.[CREDIT] || "0")}`
          }
        ]
      },
      { 
        icon: <CubeIcon className="h-4 w-4" />,
        value: `${shippingCost}`, 
        subValue: `${shippingCostEach}`, 
        subLabel: SHIPPING_COST, 
        label: "Shipping",
        withTooltip: true, 
        tooltipText: "Top number: Total amount of shipping cost <br/> Bottom number: Shipping cost each(shipping cost devided by quantity received)", 
        tooltipAlign: 'text-left', 
        gridPosition: 'top-right',
        lineItems: [
          { label: "Received", value: `${po.quantityAccepted || 0}`, compareValue: `${po.quantityOrdered || 0}` },
          { label: "Unreceived", value: `${po.missingQuantity}`, compareValue: null, additionalClass: getMissingQuantityTextColor(po.missingQuantity) },
          { label: "Shipped Quantity", value: `${po.shippedQuantity || 0}`, compareValue: null },
          // need to set the last unit price by line item
          { label: "Value", value: `$${formatPrice(po.unreceivedValue || 0)}`, compareValue: null },
          { label: "Days Late", value: `${po.daysLate || 0} Days`, compareValue: null }
        ]
      },
      
      // Second row
      { 
        icon: <DocumentPlusIcon className="h-4 w-4" />,
        label: TOTAL_VALUE, 
        value: `${po.totalValue}`, 
        withTooltip: true, 
        tooltipText: "Total value of the purchase order with shipping cost", 
        tooltipAlign: 'text-left', 
        gridPosition: 'bottom-left',
        lineItems: [
          { label: "Shipping Each", value: `$${formatPrice(shippingCostEach || 0)}`, compareValue: null },
          ...(tariffCostEach > 0 
              ? [{ label: "Tariff Each", value: `$${formatPrice(tariffCostEach || 0)}`, compareValue: null }] 
              : []),
          { label: "Unit Cost", value: `${po.unitPriceByPo}`, compareValue: null },
          { label: "Landed Cost", value: `${po.landedCost ? `${po.landedCost}` : "N/A"}`, compareValue: null }
        ]
      },
      { 
        icon: <DocumentMinusIcon className="h-4 w-4" />,
        label: TOTAL_OWED, 
        value: `${totalOwed}`, 
        additionalClass: getTotalOwedTextColor(totalOwed), 
        withTooltip: true, 
        tooltipText: "Approximate owed amount", 
        tooltipAlign: 'text-left', 
        gridPosition: 'bottom-right',
        lineItems: [
          { label: "Credit adjustment", value: `$${formatPrice(po.creditAdjustment || 0)}`, subLabel: `$${(po.paymentBarMilestones?.[CREDIT] || "0")}` }
        ]
      },
      
      // Below the grid
      // { label: QUANTITY, value: `${po.quantityAccepted} / ${po.quantityOrdered}`, gridPosition: 'below-grid' },
      // { label: LANDED_COST, value: `${po.landedCost}`, subValue: `${landedCostForecast}`, subLabel: LANDED_COST_FORECAST, withTooltip: true, tooltipText: "Top number: Total value devided by quantity received <br/> Bottom number: Total value devided by quantity ordered", tooltipAlign: 'text-left', gridPosition: 'below-grid' },
    ]
    if (po.hasShipment) {
      const skuTotal = po.skuTotal || [];
      summaryItems = summaryItems.map((item) => {
        const currentItem = { ...item };
        if (item.label === QUANTITY) {
          currentItem.withTooltip = true;
          currentItem.tooltipText = financePagePOTooltip(po);
        };
        return currentItem;
      })
    }
    return summaryItems
  }

  const renderAddPaymentButton = () => {
    // if (
    //   editingPayment || 
    //   allTypesExist(allPaymentTypes, payments) || 
    //   paymentsIsLoading() ||
    //   Number(po.totalValue) === 0
    // ) return null;
    return (
      <button
        onClick={handleAddPayment}
        disabled={!paymentStatusAllowed}
        title={paymentStatusAllowed ? "Add payment" : paymentStatusMessage('add')}
        data-testid={testId ? `add-payment-button-${testId}` : undefined}
        className="bg-black text-white px-3 py-1.5 rounded-md text-[12px] leading-relaxed hover:bg-gray-600 font-normal disabled:opacity-50 disabled:cursor-not-allowed"
      >
        Add Payment
      </button>
    )
  }

  const renderProductsTable = (po) => {
    if (!po.skuTotal || po.skuTotal.length === 0) return null;

    return (
      <div className="mb-6">
        <ul>
          {po.skuTotal.map((product, index) => (
            <li key={`${product.id}_${index}`}>
              <div>
                <span>{product.product_name}({product.product_sku})</span>
              </div>
            </li>
          ))}
        </ul>
      </div>);
  }

  const paymentsIsLoading = () => {
    return (loadingPayments) || (currentPurchaseOrdersLoadingKeys.includes(PurchaseOrdersLoadingKeys.orders))
  };

  const poIsLoading = () => {
    return (loadingPurchaseOrders) || (currentPurchaseOrdersLoadingKeys.includes(PurchaseOrdersLoadingKeys.orders))
  }

  return (
    <div 
      ref={sidebarRef}
      className="fixed top-14 md:top-0 bottom-0 right-0 w-full max-w-[690px] bg-white shadow-xl transform transition-transform duration-300 ease-in-out z-50 shadow-[-20px_0_40px_-10px_rgba(0,0,0,0.3)]" 
      data-testid={testId}
    >
      {poIsLoading() && <OverlayGrayBackground testId={testId ? `overlay-background-${testId}` : undefined} />}
      <div className="h-full flex flex-col">
          <div className="p-4 border-b border-gray-200">
            <POSidebarHeader
              editStatus={openedStatus}
              openTheStatusSelect={openTheStatusSelect}
              searchValue={searchValue}
              access={access}
              currentStatuses={statuses}
              poPage={poPage}
              poItemsPerPage={poItemsPerPage}
              currentPO={po}
              onClose={onClose}
            />
          </div>
          <div className="flex-1 overflow-y-auto p-4">
            <POSidebarInformation
              searchValue={searchValue}
              currentStatuses={statuses}
              poPage={poPage}
              access={access}
              poItemsPerPage={poItemsPerPage}
              currentPOId={currentPOId}
              currentPO={po}
            />

            <POSidebarSummaryItems summaryItems={getSummaryItems()} />

            {poLedgerDetail?.projection && (
              <div className="mb-6 rounded border border-gray-200 p-3 text-sm">
                <div className="flex items-center justify-between font-semibold">
                  <span>Freight <span className="ml-1 rounded-full bg-amber-100 px-2 py-0.5 text-xs">FORECAST</span></span>
                  {access === "edit" && (
                    <div className="flex gap-2">
                      <button
                        type="button"
                        className="text-xs text-blue-600"
                        disabled={Number(poLedgerDetail.projection.freight_billed || 0) > 0}
                        title={Number(poLedgerDetail.projection.freight_billed || 0) > 0 ? "Locked: freight billed" : "Set estimate"}
                        onClick={() => setFreightDialog({ open: true, mode: 'estimate', amount: poLedgerDetail.projection.estimated_freight || '' })}
                      >
                        Set estimate
                      </button>
                      <button
                        type="button"
                        className="text-xs text-blue-600"
                        onClick={() => setFreightDialog({ open: true, mode: 'invoice', amount: '' })}
                      >
                        Enter freight invoice
                      </button>
                    </div>
                  )}
                </div>
                <div className="mt-3 grid grid-cols-3 gap-x-4 gap-y-3">
                  {[
                    ['Estimated Freight', poLedgerDetail.projection.estimated_freight == null ? '-' : `$${formatPrice(poLedgerDetail.projection.estimated_freight)}`],
                    ['Actual Freight Billed', Number(poLedgerDetail.projection.freight_billed || 0) > 0 ? `$${formatPrice(poLedgerDetail.projection.freight_billed)}` : '-'],
                    ['Freight Variance', poLedgerDetail.projection.freight_variance == null
                      ? '-'
                      : (
                        <>
                          ${formatPrice(poLedgerDetail.projection.freight_variance)}
                          {poLedgerDetail.projection.freight_variance_pct == null
                            ? null
                            : <span className="ml-1 text-xs font-normal text-gray-500">({formatPrice(poLedgerDetail.projection.freight_variance_pct)}%)</span>}
                        </>
                      )],
                    ['Freight Paid', `$${formatPrice(poLedgerDetail.projection.freight_paid || 0)}`],
                    ['Freight Owed', `$${formatPrice(poLedgerDetail.projection.freight_owed || 0)}`],
                  ].map(([label, value]) => (
                    <div key={label}>
                      <div className="text-xs text-gray-500">{label}</div>
                      <div className="font-semibold text-gray-900">{value}</div>
                    </div>
                  ))}
                </div>
                {freightActionError && (
                  <div className="mt-2 rounded bg-red-50 px-2 py-1 text-xs text-red-700">{freightActionError}</div>
                )}
                {(poLedgerDetail.open_exceptions || [])
                  .filter((item) => item.check_type === 'FREIGHT_ESTIMATE_REVIEW')
                  .map((item) => (
                    <div key={item.id} className="mt-2 rounded border border-amber-300 bg-amber-50 p-2 text-xs text-amber-900">
                      <div>PO changed after estimate.</div>
                      <div className="mt-1 flex gap-2">
                        <button
                          type="button"
                          className="underline"
                          onClick={() => runFreightAction(() => axios.post(`/api/v1/po-ledger/${po.id}/freight-estimate`, {
                            amount: poLedgerDetail.projection.estimated_freight,
                            source: 'KEPT_AFTER_PO_CHANGE',
                            reason: 'Kept after PO goods change',
                          }))}
                        >
                          Keep estimate
                        </button>
                        <button type="button" className="underline" onClick={() => setFreightDialog({
                          open: true,
                          mode: 'estimate',
                          amount: poLedgerDetail.projection.estimated_freight || '',
                        })}>
                          Update estimate
                        </button>
                      </div>
                    </div>
                  ))}
                {(poLedgerDetail.open_exceptions || [])
                  .filter((item) => item.check_type === 'FREIGHT_PAID_WITHOUT_BILL')
                  .map((item) => (
                    <div key={item.id} className="mt-2 rounded border border-red-300 bg-red-50 p-2 text-xs text-red-900">
                      <div>Shipping payment recorded with no freight invoice.</div>
                      <button
                        type="button"
                        className="mt-1 underline"
                        onClick={() => setFreightDialog({ open: true, mode: 'invoice', amount: '' })}
                      >
                        Enter freight invoice
                      </button>
                    </div>
                  ))}
                <div className="mt-2 border-t pt-2 text-xs text-gray-500">
                  {Number(poLedgerDetail.projection.freight_billed || 0) > 0
                    ? 'Projected Cash Out (Total Owed, freight billed)'
                    : 'Projected Cash Out (Total Owed + Est. Freight)'}
                  <span className="float-right font-semibold text-gray-900">${formatPrice(poLedgerDetail.projection.forecast_remaining || 0)}</span>
                </div>
                {poLedgerDetail.freight_events?.length > 0 && (
                  <details className="mt-3">
                    <summary className="cursor-pointer text-xs text-gray-600">Freight history</summary>
                    <div className="mt-2 space-y-1 text-xs">
                      {poLedgerDetail.freight_events.map((event) => (
                        <div key={event.id} className="flex justify-between">
                          <span>
                            {formatFreightDate(event.effective_date)} {event.event_type === 'FREIGHT_ESTIMATE' ? 'Estimate' : event.event_type === 'PAYMENT' ? 'Payment' : 'Invoice'}
                            {event.meta_json?.invoice_number ? ` #${event.meta_json.invoice_number}` : ''}
                            {event.reason_code ? ` (${event.reason_code})` : ''}
                          </span>
                          <span>${formatPrice(event.amount)}</span>
                        </div>
                      ))}
                    </div>
                  </details>
                )}
              </div>
            )}

            <div className="mb-6">
              <h3 className="text-base font-bold mb-2">Payment Progress</h3>
              <PaymentBar 
                payments={payments} 
                paymentBarMilestones={po.paymentBarMilestones || {}} 
                paymentBarPercentages={po.paymentBarPercentages || {}}
                totalValue={po.totalValue} 
              />
            </div>

            <div className="mt-6">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-base font-bold">Payments</h3>
                {access === "edit" && renderAddPaymentButton()}
              </div>
              {paymentsIsLoading() ?
                (
                  <div className="flex justify-center w-full mt-6">
                    <LoaderSpinner testId={testId ? `table-loader-${testId}` : undefined} />
                  </div>
                ) : (
                  <>
                    {/* Mobile payment cards */}
                    <div className="sm:hidden space-y-2">
                      {payments.length === 0 && (
                        <p className="text-sm text-gray-500 py-1">No payments recorded yet.</p>
                      )}
                      {payments.map((payment, index) => (
                        <div key={`mobile_${payment.id}_${index}`} className="rounded-md border border-borderTable p-3">
                          <div className="flex items-center gap-2">
                            <span className="text-base font-bold">${formatPrice(payment.amount)}</span>
                            <span className="px-1.5 py-0.5 rounded bg-tableHeader text-xs font-medium">
                              {enumPaymentTypes[payment.type]}
                            </span>
                            {access === "edit" && (
                              <button
                                onClick={() => handleEditClick(payment)}
                                disabled={!paymentStatusAllowed}
                                title={paymentStatusAllowed ? "Edit payment" : paymentStatusMessage('edit')}
                                className="ml-auto p-1 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                                aria-label="Edit payment"
                              >
                                <PencilSquareIcon className="h-4 w-4" />
                              </button>
                            )}
                          </div>
                          <div className="mt-1 text-xs text-gray-500 flex flex-col gap-0.5">
                            <span>{formatDateToShortLabel(payment.date)}</span>
                            {payment.transactionId && (
                              <button
                                onClick={() => handleCopyEvent(payment.transactionId)}
                                className="flex items-center text-blue-600 hover:text-blue-800 w-fit"
                              >
                                {payment.transactionId}
                                <ClipboardDocumentIcon className="h-3.5 w-3.5 ml-1" />
                              </button>
                            )}
                            {payment.note && <span className="text-gray-700">{payment.note}</span>}
                          </div>
                        </div>
                      ))}
                    </div>
                    {/* Desktop payments table */}
                    <div className="hidden sm:block overflow-y-auto">
                      <table className="min-w-full relative action-payment-table" data-testid={testId ? `table-${testId}` : undefined}>
                        <thead>
                          <tr className="text-left text-gray-500">
                            <th className="px-3 py-1.5 text-sm font-medium">Date</th>
                            <th className="px-3 py-1.5 text-sm font-medium">Amount</th>
                            <th className="px-3 py-1.5 text-sm font-medium">Type</th>
                            <th className="px-3 py-1.5 text-sm font-medium">Transaction</th>
                            <th className="px-3 py-1.5 text-sm font-medium">Note</th>
                            {access === "edit" && (
                              <th className="w-8 px-2 py-1.5"></th>
                            )}
                          </tr>
                        </thead>
                        <tbody className="bg-white">
                          {payments.map((payment, index) => (
                            <tr key={`${payment.id}_${index}`} className="border-b border-borderTable group">
                              <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500">
                                {formatDateToShortLabel(payment.date)}
                              </td>
                              <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900 font-medium">
                                {`$${formatPrice(payment.amount)}`}
                              </td>
                              <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500">
                                {enumPaymentTypes[payment.type]}
                              </td>
                              <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500">
                                {payment.transactionId && (
                                  <button
                                    onClick={() => handleCopyEvent(payment.transactionId)}
                                    className="flex items-center text-blue-600 hover:text-blue-800"
                                  >
                                    {`${payment.transactionId?.length > 6 ? `${payment.transactionId?.slice(0, 6)}` : payment.transactionId}`}
                                    <ClipboardDocumentIcon className="h-4 w-4 ml-1" />
                                  </button>
                                )}
                              </td>
                              <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500">
                                {payment.note && (
                                  <button
                                    onClick={() => handleCopyEvent(payment.note)}
                                    className="flex items-center text-blue-600 hover:text-blue-800"
                                  >
                                    {`${payment.note?.length > 6 ? `${payment.note?.slice(0, 6)}` : payment.note}`}
                                    <ClipboardDocumentIcon className="h-4 w-4 ml-1" />
                                  </button>
                                )}
                              </td>
                              {access === "edit" && (
                                <td className="px-2 py-2 whitespace-nowrap text-sm text-gray-500 text-right">
                                  <button
                                    onClick={() => handleEditClick(payment)}
                                    disabled={!paymentStatusAllowed}
                                    title={paymentStatusAllowed ? "Edit payment" : paymentStatusMessage('edit')}
                                    className="p-1 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                                    aria-label="Edit payment"
                                  >
                                    <PencilSquareIcon className="h-4 w-4 cursor-pointer" />
                                  </button>
                                </td>
                              )}
                            </tr>
                          ))}
                          {payments.length === 0 && (
                            <tr>
                              <td colSpan={access === "edit" ? "6" : "5"} className="px-3 py-3 text-sm text-gray-500 text-center">
                                No payments recorded yet.
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}
            </div>
            {access === "edit" && (
              <PaymentDialog
                open={!!editingPayment}
                po={po}
                initialPayment={editingPayment}
                onSave={(paymentForm) => handleSaveClick(paymentForm)}
                onDelete={(paymentId) => handleDeletePayment(paymentId)}
                onClose={handleCancelClick}
                testId={testId ? `payment-dialog-${testId}` : undefined}
              />
            )}
            <POSidebarNotes
              poId={po.id}
              access={access}
            />
            <POSidebarAuditTrail poId={po.id} />
            {(po.skuTotal || []).length > 0 && (
              <div className="mt-6">
                <h3 className="text-base font-bold mb-2">Line Items</h3>
                <div className="overflow-x-auto rounded-md border border-borderTable">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-tableHeader">
                      <tr>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Product</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">SKU</th>
                        <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Price</th>
                        <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Qty</th>
                        <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Received</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-100">
                      {(po.skuTotal || []).map((product, index) => (
                        <tr key={`${product.product_id}_${index}`}>
                          <td className="px-3 py-2 text-sm text-gray-900">{product.product_name}</td>
                          <td className="px-3 py-2">
                            <span className="px-1.5 py-0.5 rounded bg-tableHeader text-xs font-mono">{product.product_sku}</span>
                          </td>
                          <td className="px-3 py-2 text-sm text-right text-gray-900">${formatPrice(product.price || 0)}</td>
                          <td className="px-3 py-2 text-sm text-right text-gray-900">{product.total_box_qty || 0}</td>
                          <td className="px-3 py-2 text-sm text-right text-gray-900">{product.total_box_received || 0}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
            <div className="mt-6">
              <AttachmentsSection
                access={access}
                initialFiles={po.uploadedFiles}
                referenceType="PurchaseOrder"
                referenceId={po.id}
                onFilesChange={(files) => dispatch(updatePOById({ poId: po.id, updatedData: { uploadedFiles: files } }))}
              />
            </div>
          </div>
      </div>
      <FreightDialog
        open={freightDialog.open}
        poId={po.id}
        mode={freightDialog.mode}
        initialAmount={freightDialog.amount}
        onClose={() => setFreightDialog((current) => ({ ...current, open: false }))}
        onSubmitted={async () => {
          await refreshFreightDetail();
          await refreshPurchaseOrders();
        }}
      />
    </div>
  );
};
